import EmblaCarousel from "embla-carousel";

class HoverCarousel {
  constructor(el) {
    this.el = el;
    this.init();
  }

  init() {
    const viewport = this.el.querySelector(".embla__viewport");

    // Get configuration from data attributes
    const randomizeAttr = this.el.getAttribute("data-randomize");
    const randomize = randomizeAttr === "true" || randomizeAttr === true;
    const maxSlides = parseInt(
      this.el.getAttribute("data-max-slides") || "6",
      10,
    );

    // Get all slides and convert to array
    const container = viewport.querySelector(".embla__container");
    const allSlides = Array.from(this.el.querySelectorAll(".embla__slide"));

    // Filter slides client-side: shuffle if needed, then slice
    let selectedSlides = [...allSlides];
    if (randomize) {
      // Fisher-Yates shuffle - start from the end and work backwards
      for (let i = selectedSlides.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [selectedSlides[i], selectedSlides[j]] = [
          selectedSlides[j],
          selectedSlides[i],
        ];
      }
    }
    selectedSlides = selectedSlides.slice(0, maxSlides);

    // Remove all slides from DOM temporarily to allow proper reordering
    allSlides.forEach((slide) => {
      slide.remove();
    });

    // Re-add selected slides in the correct (possibly shuffled) order
    selectedSlides.forEach((slide) => {
      container.appendChild(slide);
    });

    const options = {
      loop: true,
      align: "center",
      containScroll: false,
      dragFree: false,
      slidesToScroll: 1,
      watchDrag: true, // default on mobile
      breakpoints: {
        "(min-width: 1024px)": { watchDrag: false }, // disable drag on desktop
      },
    };

    this.slides = this.el.querySelectorAll(".embla__slide");
    this.totalSlides = this.slides.length;
    this.updateSlideCounts(selectedSlides.length);

    this.setupNavigation();
    this.setupMouseTracking();
    this.createTooltip();
    this.updateActiveTooltip();

    // Initialize Embla
    this.embla = EmblaCarousel(viewport, options);
  }

  updateSlideCounts(totalCount) {
    // Update any count displays in the slides (e.g., "1—6" format)
    this.slides.forEach((slide, index) => {
      // Find the first paragraph in the grid section that contains the count
      const gridSection = slide.querySelector(".grid p");
      if (gridSection && gridSection.textContent.includes("—")) {
        const currentText = gridSection.textContent;
        const newText = currentText.replace(
          /\d+—\d+/,
          `${index + 1}—${totalCount}`,
        );
        gridSection.textContent = newText;
      }
    });
  }

  setupNavigation() {
    const prevBtn = this.el.querySelector(".embla__button--prev");
    const nextBtn = this.el.querySelector(".embla__button--next");

    if (prevBtn && nextBtn) {
      prevBtn.addEventListener("click", () => this.embla.scrollPrev());
      nextBtn.addEventListener("click", () => this.embla.scrollNext());

      this.embla.on("select", () => {
        prevBtn.disabled = !this.embla.canScrollPrev();
        nextBtn.disabled = !this.embla.canScrollNext();
      });

      // Initialize button states
      this.embla.on("init", () => {
        prevBtn.disabled = !this.embla.canScrollPrev();
        nextBtn.disabled = !this.embla.canScrollNext();
      });
    }
  }

  setupMouseTracking() {
    this.isDesktop = window.innerWidth > 800;
    if (!this.isDesktop) return;

    this.lastIndex = -1;
    this.mouseSlideUpdate = this.mouseSlideUpdate.bind(this);
    this.frame = requestAnimationFrame(this.mouseSlideUpdate);
  }

  createTooltip() {
    this.isDesktop = window.innerWidth > 800;
    if (!this.isDesktop) return;

    // Track if mouse has moved to prevent premature visibility
    this.hasMouseMoved = false;

    // Create tooltip container
    this.tooltip = document.createElement("a");
    this.tooltip.setAttribute("href", "#");
    this.tooltip.setAttribute("role", "button");
    this.tooltip.setAttribute("aria-label", "More");
    this.tooltip.className = [
      "theme-dark",
      "hc-tooltip",
      "fixed",
      "z-[10]",
      "flex",
      "justify-center",
      "text-foreground",
      "rounded-full",
      "border-1",
      "type-62-sans",
      "bg-background/10",
      "backdrop-blur",
      "transition-opacity",
      "duration-150",
      "opacity-0",
      "pointer-events-none",
      "max-lg:hidden",
      "size-[2em]",
    ].join(" ");

    this.tooltipText = document.createElement("span");
    this.tooltipText.className = "w-full h-full";
    this.tooltipText.textContent = "";
    this.tooltip.appendChild(this.tooltipText);

    document.body.appendChild(this.tooltip);

    this._tooltipHover = false;
    this.tooltip.addEventListener("mouseenter", () => {
      this._tooltipHover = true;
      this.tooltip.style.opacity = "1";
    });
    this.tooltip.addEventListener("mouseleave", () => {
      this._tooltipHover = false;
      // If we already left the carousel too, hide
      if (this._leftCarousel) {
        this.tooltip.style.opacity = "0";
      }
    });

    this._leftCarousel = false;
    this._onEnter = () => {
      this._leftCarousel = false;
      // Only show tooltip if mouse has moved and there's a URL
      if (this.hasMouseMoved && this.currentUrl) {
        this.tooltip.style.opacity = "1";
      }
    };
    this._onLeave = () => {
      this._leftCarousel = true;
      // Delay a bit to allow clicking the tooltip that may sit just outside the carousel
      setTimeout(() => {
        if (!this._tooltipHover) {
          this.tooltip.style.opacity = "0";
        }
      }, 120);
    };

    this.el.addEventListener("mouseenter", this._onEnter);
    this.el.addEventListener("mouseleave", this._onLeave);

    // Click anywhere on the carousel to go to current URL
    this._onRootClick = (e) => {
      const url = this.currentUrl;
      if (!url) return;
      // ignore clicks on nav buttons or any link inside
      if (e.target.closest(".embla__button, a")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        window.open(url, "_blank");
        return;
      }
      e.preventDefault();
      window.location.assign(url);
    };
    this.el.addEventListener("click", this._onRootClick);

    // Follow cursor
    this._onMove = (e) => {
      // Mark that mouse has moved
      this.hasMouseMoved = true;

      const viewportWidth = window.innerWidth;
      const tooltipWidth = this.tooltip.offsetWidth || 120; // Fallback width estimate
      const edgeThreshold = 200; // Distance from right edge to trigger position change

      // Check if cursor is near the right edge
      const isNearRightEdge =
        e.clientX + tooltipWidth + 16 > viewportWidth - edgeThreshold;

      let x, y;

      if (isNearRightEdge) {
        // Position tooltip at bottom left of cursor
        x = e.clientX - tooltipWidth - 16;
        y = e.clientY + 16;
      } else {
        // Default position: top right of cursor
        x = e.clientX + 16;
        y = e.clientY + 16; // Slightly above cursor
      }

      // Ensure tooltip doesn't go off-screen
      x = Math.max(16, Math.min(x, viewportWidth - tooltipWidth - 16));
      y = Math.max(16, y);

      this.tooltip.style.left = x + "px";
      this.tooltip.style.top = y + "px";
    };
    document.addEventListener("mousemove", this._onMove);
    this.updateActiveTooltip();
  }

  updateActiveTooltip() {
    if (!this.isDesktop) return;

    const snap = this.embla ? this.embla.selectedScrollSnap() : 0;
    const idx = Math.max(0, Math.min(this.totalSlides - 1, snap));
    const slide = this.slides[idx];
    if (!slide) return;

    const name = slide.getAttribute("data-company-name") || "Company";
    const url = slide.getAttribute("data-company-url") || "";
    const type = slide.getAttribute("data-company-logo-type") || "text";
    const alt = slide.getAttribute("data-company-logo-alt") || name;

    const svgContent = slide.getAttribute("data-company-logo-content") || "";
    const imgUrl = slide.getAttribute("data-company-logo-url") || "";

    this.currentUrl = url;

    // Clear previous tooltip content
    this.tooltipText.innerHTML = "";

    if (type === "svg" && svgContent) {
      // svgContent is the raw SVG markup (already entity-decoded by the browser)
      this.tooltipText.innerHTML = svgContent;

      // Constrain the inline SVG sizing
      const svgEl = this.tooltipText.querySelector("svg");
      if (svgEl) {
        svgEl.setAttribute("aria-label", alt);
      }
    } else if (imgUrl) {
      const img = document.createElement("img");
      img.src = imgUrl;
      img.alt = alt;
      this.tooltipText.appendChild(img);
    } else {
      // Fallback to text
      this.tooltipText.textContent = alt;
    }

    this.tooltip.setAttribute("aria-label", `View ${alt}`);
    if (url) {
      this.tooltip.setAttribute("href", url);
      if (this.hasMouseMoved && !this._leftCarousel) {
        this.tooltip.style.opacity = "1";
      }
    } else {
      this.tooltip.setAttribute("href", "#");
    }
  }

  // 🔽 NEW: Read --mouse-x and scroll accordingly
  mouseSlideUpdate() {
    const rootStyles = getComputedStyle(document.documentElement);
    const mouseX = parseFloat(rootStyles.getPropertyValue("--mouse-x")) || 0;
    const viewportWidth = window.innerWidth;

    const percentage = Math.max(0, Math.min(1, mouseX / viewportWidth));
    const slideIndex = Math.floor(percentage * this.totalSlides);

    if (this.embla && slideIndex !== this.lastIndex) {
      this.embla.scrollTo(slideIndex, true);
      this.lastIndex = slideIndex;
      this.updateActiveTooltip();
    }

    this.frame = requestAnimationFrame(this.mouseSlideUpdate);
  }

  // ✅ Clean up the animation frame
  destroy() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
    if (this._onMove) document.removeEventListener("mousemove", this._onMove);
    if (this._onEnter) this.el.removeEventListener("mouseenter", this._onEnter);
    if (this._onLeave) this.el.removeEventListener("mouseleave", this._onLeave);
    if (this._onRootClick)
      this.el.removeEventListener("click", this._onRootClick);
    if (this.tooltip) this.tooltip.remove();
    if (this.embla) {
      this.embla.destroy();
    }
  }

  disconnect() {
    this.destroy();
  }
}

export default HoverCarousel;
